import React, { useEffect, useState } from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import {
  getPlatformAudit,
  getPlatformHealth,
  getSignupSummary,
  getSignupTimeseries,
  getUserActivity,
} from '../../Api/Platform';
import TrinityDashboardModule, { loadExpandedModules, saveExpandedModules } from './TrinityDashboardModule';
import ExpiringSubscriptionsPanel from './ExpiringSubscriptionsPanel';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const metricCards = (activity) => ([
  { key: 'logged_in_4h', label: 'Logged in last 4h', value: activity?.logged_in_4h },
  { key: 'logged_in_24h', label: 'Logged in last 24h', value: activity?.logged_in_24h },
  { key: 'logged_in_7d', label: 'Logged in last 7d', value: activity?.logged_in_7d },
  { key: 'logged_in_30d', label: 'Logged in last 30d', value: activity?.logged_in_30d },
  { key: 'never_logged_in', label: 'Never logged in', value: activity?.never_logged_in },
  { key: 'verified_users', label: 'Verified users', value: activity?.verified_users },
  { key: 'total_users', label: 'Total users', value: activity?.total_users },
  { key: 'total_orgs', label: 'Total orgs', value: activity?.total_orgs },
  { key: 'paid_orgs', label: 'Paid orgs', value: activity?.paid_orgs },
  { key: 'trial_orgs', label: 'Trial orgs', value: activity?.trial_orgs },
  { key: 'expired_orgs', label: 'Expired trials', value: activity?.expired_orgs },
  { key: 'onboarding_incomplete', label: 'Onboarding incomplete', value: activity?.onboarding_incomplete },
]);

const TrinityDashboard = () => {
  const [health, setHealth] = useState(null);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [activity, setActivity] = useState(null);
  const [audit, setAudit] = useState([]);
  const [expanded, setExpanded] = useState(loadExpandedModules);

  const setModule = (id, next) => {
    setExpanded((prev) => {
      const updated = { ...prev, [id]: next };
      saveExpandedModules(updated);
      return updated;
    });
  };

  useEffect(() => {
    getPlatformHealth().then((res) => setHealth(res?.data)).catch(() => setHealth({ status: 'error' }));
    getSignupSummary().then((res) => setSummary(res?.data)).catch(() => {});
    getSignupTimeseries({ bucket: 'day' }).then((res) => setSeries(res?.data || [])).catch(() => {});
    getUserActivity().then((res) => setActivity(res?.data)).catch(() => {});
    getPlatformAudit({ limit: 8 }).then((res) => setAudit(res?.data?.data || [])).catch(() => {});
  }, []);

  const chartData = {
    labels: series.map((p) => p.date),
    datasets: [
      {
        label: 'New organisations',
        data: series.map((p) => p.count),
        borderColor: '#1D3DFC',
        backgroundColor: 'rgba(29, 61, 252, 0.12)',
        tension: 0.25,
      },
    ],
  };

  return (
    <Box>
      <TrinityDashboardModule
        title="Platform health"
        summary={health?.status ? String(health.status) : 'loading'}
        expanded={!!expanded.health}
        onChange={(next) => setModule('health', next)}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {(health?.components || []).map((c) => (
            <Paper key={c.name} variant="outlined" sx={{ p: 2, minWidth: 220 }}>
              <Typography variant="subtitle2">{c.name}</Typography>
              <Chip size="small" label={c.status} color={c.status === 'ok' ? 'success' : 'warning'} sx={{ mt: 1 }} />
              {c.latency ? (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#6B7280' }}>
                  {c.latency}
                </Typography>
              ) : null}
            </Paper>
          ))}
          <Paper variant="outlined" sx={{ p: 2, minWidth: 160 }}>
            <Typography variant="h4">{summary?.this_week ?? '—'}</Typography>
            <Typography variant="caption" color="text.secondary">Orgs this week</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, minWidth: 160 }}>
            <Typography variant="h4">{summary?.prior_week ?? '—'}</Typography>
            <Typography variant="caption" color="text.secondary">Prior week</Typography>
          </Paper>
        </Box>
      </TrinityDashboardModule>

      <TrinityDashboardModule
        title="User activity"
        summary={activity ? `${activity.logged_in_4h ?? 0} in last 4h` : 'loading'}
        expanded={!!expanded.users}
        onChange={(next) => setModule('users', next)}
      >
        <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 1.5 }}>
          Counts last password login, not live sessions. Switch-account and SSO do not update this timestamp.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {metricCards(activity).map((card) => (
            <Paper key={card.key} variant="outlined" sx={{ p: 2, minWidth: 150 }}>
              <Typography variant="h4">{card.value ?? '—'}</Typography>
              <Typography variant="caption" color="text.secondary">{card.label}</Typography>
            </Paper>
          ))}
        </Box>
      </TrinityDashboardModule>

      <TrinityDashboardModule
        title="Organisation signups"
        summary={`${series.reduce((sum, p) => sum + (p.count || 0), 0)} in range`}
        expanded={!!expanded.signups}
        onChange={(next) => setModule('signups', next)}
      >
        <Paper variant="outlined" sx={{ p: 2, maxWidth: 880 }}>
          <Line
            data={chartData}
            options={{
              responsive: true,
              plugins: { legend: { display: true } },
              scales: {
                x: { title: { display: true, text: 'Date' } },
                y: { title: { display: true, text: 'Organisations created' }, beginAtZero: true },
              },
            }}
          />
        </Paper>
      </TrinityDashboardModule>

      <ExpiringSubscriptionsPanel
        expanded={!!expanded.expiring}
        onChange={(next) => setModule('expiring', next)}
      />

      <TrinityDashboardModule
        title="Recent platform audit"
        summary={`${audit.length} latest`}
        expanded={!!expanded.audit}
        onChange={(next) => setModule('audit', next)}
      >
        <Paper variant="outlined">
          {audit.length === 0 ? (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">No audit rows</Typography>
            </Box>
          ) : audit.map((row) => (
            <Box key={row.id} sx={{ display: 'flex', gap: 2, px: 2, py: 1.25, borderBottom: '1px solid #F3F4F6' }}>
              <Typography sx={{ width: 200 }}>{new Date(row.created_at).toLocaleString()}</Typography>
              <Typography sx={{ width: 220 }}>{row.actor_email}</Typography>
              <Typography sx={{ flex: 1 }}>{row.action}</Typography>
              <Typography sx={{ width: 220 }}>{row.resource}</Typography>
            </Box>
          ))}
        </Paper>
      </TrinityDashboardModule>
    </Box>
  );
};

export default TrinityDashboard;
